import dayjs from "@/plugins/dayjs";

/**
 * Type pour fonction exemple dans un token de pattern de date
 */
type ExampleFn = (d: Date) => string;

/**
 * Structure pour chaque pattern de la map
 */
interface PatternToken {
  regex: string;
  mask: string;
  example: ExampleFn;
}

type PatternMap = Record<string, PatternToken>;

const referenceDate = new Date("2004-01-23T12:34:56.789Z");

// Déclaration détaillée (en Typescript) pour la map de patterns
const patternMap: PatternMap = {
  yyyy: { regex: "\\d{4}", mask: "9999", example: d => String(d.getFullYear()) },
  yy: { regex: "\\d{2}", mask: "99", example: d => String(d.getFullYear()).slice(-2) },
  MMMM: {
    regex: "[A-Za-zéûîôàèùç\\.]+",
    mask: "AAAA",
    example: d => d.toLocaleString("fr", { month: "long" }),
  },
  MMM: {
    regex: "[A-Za-zéûîôàèùç\\.]{3,6}",
    mask: "AAA",
    example: d => d.toLocaleString("fr", { month: "short" }),
  },
  MM: {
    regex: "(0[1-9]|1[0-2])",
    mask: "99",
    example: d => String(d.getMonth() + 1).padStart(2, "0"),
  },
  M: { regex: "([1-9]|1[0-2])", mask: "9", example: d => String(d.getMonth() + 1) },
  dd: {
    regex: "(0[1-9]|[12][0-9]|3[01])",
    mask: "99",
    example: d => String(d.getDate()).padStart(2, "0"),
  },
  d: { regex: "([1-9]|[12][0-9]|3[01])", mask: "9", example: d => String(d.getDate()) },
  HH: {
    regex: "([01][0-9]|2[0-3])",
    mask: "99",
    example: d => String(d.getHours()).padStart(2, "0"),
  },
  H: { regex: "([0-9]|1[0-9]|2[0-3])", mask: "9", example: d => String(d.getHours()) },
  hh: {
    regex: "(0[1-9]|1[0-2])",
    mask: "99",
    example: d => String(d.getHours() % 12 || 12).padStart(2, "0"),
  },
  h: { regex: "([1-9]|1[0-2])", mask: "9", example: d => String(d.getHours() % 12 || 12) },
  mm: {
    regex: "([0-5][0-9])",
    mask: "99",
    example: d => String(d.getMinutes()).padStart(2, "0"),
  },
  m: { regex: "([0-9]|[1-5][0-9])", mask: "9", example: d => String(d.getMinutes()) },
  ss: {
    regex: "([0-5][0-9])",
    mask: "99",
    example: d => String(d.getSeconds()).padStart(2, "0"),
  },
  s: { regex: "([0-9]|[1-5][0-9])", mask: "9", example: d => String(d.getSeconds()) },
  SSS: {
    regex: "\\d{3}",
    mask: "999",
    example: d => String(d.getMilliseconds()).padStart(3, "0"),
  },
  S: { regex: "\\d{1,3}", mask: "9", example: d => String(d.getMilliseconds()) },
  EEEE: {
    regex: "[A-Za-zéûîôàèùç\\.]+",
    mask: "AAAA",
    example: d => d.toLocaleString("fr", { weekday: "long" }),
  },
  EEE: {
    regex: "[A-Za-zéûîôàèùç\\.]{2,6}",
    mask: "AAA",
    example: d => d.toLocaleString("fr", { weekday: "short" }),
  },
  E: {
    regex: "[A-Za-zéûîôàèùç\\.]{1,6}",
    mask: "A",
    example: d => d.toLocaleString("fr", { weekday: "narrow" }),
  },
  a: { regex: "(AM|PM)", mask: "AA", example: d => (d.getHours() < 12 ? "AM" : "PM") },
  D: {
    regex: "([1-9]|[12][0-9]{1,2}|3[0-6][0-6])",
    mask: "999",
    example: (d) => {
      const start = new Date(d.getFullYear(), 0, 0);
      const diff = d.getTime() - start.getTime();
      const oneDay = 1000 * 60 * 60 * 24;
      return String(Math.floor(diff / oneDay));
    },
  },
  F: { regex: "[1-5]", mask: "9", example: d => String(Math.ceil(d.getDate() / 7)) },
  w: {
    regex: "([1-9]|[1-4][0-9]|5[0-3])",
    mask: "99",
    example: (d) => {
      const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const start = new Date(date.getFullYear(), 0, 1);
      const diff = ((date.getTime() - start.getTime()) / 86400000 + start.getDay() + 1) / 7;
      return String(Math.floor(diff));
    },
  },
  W: { regex: "[1-5]", mask: "9", example: d => String(Math.ceil(d.getDate() / 7)) },
  z: { regex: "[A-Za-z/\\+\\-0-9]+", mask: "AAAA", example: () => "CET" },
  Z: { regex: "[\\+\\-]\\d{4}", mask: "AAAA", example: () => "+0200" },
};

function escapeRegex(str: string): string {
  return str.replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&");
}

/**
 * Entrée typé pour la fonction de formatage
 */
type FormatInput = Date | string | number | (number | string)[] | null | undefined;

interface ParsePatternResult {
  regexp: RegExp;
  mask: string;
  example: string;
  formatted: (input: FormatInput) => string;
}

/**
 * Hook typé pour la gestion des patterns de date personnalisés
 */
export function useDateFormatting() {
  /**
   * Convertit un pattern Java (genre SimpleDateFormat) en pattern dayjs
   */
  const convertJavaToMomentPattern = function (javaPattern: string): string {
    return javaPattern
      .replace(/'([^']+)'/g, "[$1]")
      .replace(/yyyy/g, "YYYY")
      .replace(/MM/g, "MM")
      .replace(/dd/g, "DD")
      .replace(/HH/g, "HH")
      .replace(/mm/g, "mm")
      .replace(/ss/g, "ss");
  };

  function parsePattern(pattern: string, date: Date = referenceDate): ParsePatternResult {
    const dayjsPattern = convertJavaToMomentPattern(pattern);
    const sortedKeys = Object.keys(patternMap).sort((a, b) => b.length - a.length);
    let regex = "";
    let mask = "";
    let example = "";
    let i = 0;
    while (i < pattern.length) {
      if (pattern[i] === "'") {
        let j = i + 1;
        let literal = "";
        while (j < pattern.length && pattern[j] !== "'") {
          literal += pattern[j++];
        }
        regex += escapeRegex(literal);
        mask += literal;
        example += literal;
        i = j + 1;
      }
      else {
        let matched = false;
        for (const key of sortedKeys) {
          if (pattern.startsWith(key, i)) {
            const token = patternMap[key];
            regex += token.regex;
            mask += token.mask;
            example += token.example(date);
            i += key.length;
            matched = true;
            break;
          }
        }
        if (!matched) {
          regex += escapeRegex(pattern[i]);
          mask += pattern[i];
          example += pattern[i];
          i++;
        }
      }
    }

    return {
      regexp: new RegExp(`^${regex}$`),
      mask,
      example,
      formatted: (input: FormatInput): string => {
        if (input === "" || input === null || input === undefined) {
          return "";
        }
        let utcDayjs: dayjs.Dayjs;
        if (Array.isArray(input)) {
          const [year = 0, month = 0, day = 1, hours = 0, minutes = 0, seconds = 0, ms = 0] = input as number[];
          utcDayjs = dayjs.utc(new Date(Date.UTC(year, month, day, hours, minutes, seconds, ms)));
        }
        else if (typeof input === "string") {
          const normalized = input.endsWith("Z") ? input : `${input}Z`;
          utcDayjs = dayjs.utc(normalized);
        }
        else if (input instanceof Date) {
          utcDayjs = dayjs.utc(input);
        }
        else {
          utcDayjs = dayjs.utc(input as dayjs.ConfigType);
        }
        return utcDayjs.format(dayjsPattern);
      },
    };
  }

  return {
    parsePattern,
  };
}
